// import { ImagePlaceholder } from "";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Nhịp chuyển sang lượt đoán kế tiếp trong ticker (ms).
const TICKER_INTERVAL_MS = 3000;

/** Dòng thông báo lượt đoán gần nhất (ticker). */
/**
 * {
    "success": true,
    "message": null,
    "data": [
        {
            "key": 0,
            "auction_time": "2026-02-09T17:00:00.000Z",
            "price": "***",
            "isdn": "977****",
            "name": null,
            "image": null,
            "status": 1
        },
        {
            "key": 1,
            "auction_time": "2026-02-09T17:00:00.000Z",
            "price": "***",
            "isdn": "966****",
            "name": null,
            "image": null,
            "status": 1
        },
        {
            "key": 2,
            "auction_time": "2026-02-09T17:00:00.000Z",
            "price": "***",
            "isdn": "966****",
            "name": null,
            "image": null,
            "status": 1
        }
    ],
    "total": 3,
    "pageSize": 7,
    "current": 1
}
 */
const GuessTicker = ({ bidHistory }) => {
  const { t } = useTranslation();
  const { data } = bidHistory || {};

  // Danh sách số điện thoại để chạy ticker; fallback về phone mặc định khi chưa
  // có data từ API.
  const phones = useMemo(() => {
    const list = (data || []).map((item) => item?.isdn).filter(Boolean);
    return list.length > 0 ? list : [];
  }, [data]);

  // index lượt đoán đang hiển thị; đổi index cũng là "khoá" (key) để React
  // remount <p> -> chạy lại animation bump-in cho từng item.
  const [index, setIndex] = useState(0);

  // Danh sách đổi (data mới về) thì quay lại đầu.
  useEffect(() => {
    setIndex(0);
  }, [phones]);

  // Cứ mỗi nhịp thì chuyển sang lượt đoán kế tiếp (vòng lặp).
  useEffect(() => {
    if (phones.length <= 1) return undefined;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % phones.length);
    }, TICKER_INTERVAL_MS);
    return () => clearInterval(id);
  }, [phones]);

  const currentPhone = phones[index] ?? "";
  // console.log( "currentPhone", currentPhone)
  const currentPrice = data?.[index]?.price ?? "***";
  const currentImage = data?.[index]?.image || "";
  const message = t("home.ticker.justGuessed", { phone: currentPhone, price: currentPrice });

  // @container để pill co giãn theo bề rộng hàng; -my kéo ticker sát hơn với
  // card phía trên/dưới (bỏ mt-5 cũ khiến nó lọt thỏm giữa hai component).
  return (
    <section className="@container -my-1.5 flex justify-center">
      {/* key theo index -> React remount cả pill mỗi lượt để chạy lại bump-in */}
      <div
        key={index}
        className="relative flex animate-bump-in items-center gap-[clamp(6px,1.5cqw,10px)] rounded-full bg-linear-to-r from-[#F2000B]/0 via-[#FF4D11] to-[#F2000B]/0 px-[clamp(20px,5cqw,40px)] py-[clamp(4px,0.7cqw,6px)] before:absolute before:inset-x-0 before:top-0 before:h-0.75 before:bg-linear-to-r before:from-[#FAECA9]/0 before:via-[#FAECA9] before:to-[#FAECA9]/0 before:content-[''] after:absolute after:inset-x-0 after:bottom-0 after:h-0.75 after:bg-linear-to-r after:from-[#FAECA9]/0 after:via-[#FAECA9] after:to-[#FAECA9]/0 after:content-['']"
      >
        {/* Avatar đầu dòng — cùng kỹ thuật xếp lớp như chữ: lớp dưới #B7220D làm
            viền + đổ bóng, lớp trên là ảnh (chừa 2px quanh mép để lộ viền đỏ). */}
        <span className="inline-grid size-[clamp(28px,4.5cqw,36px)] shrink-0">
          <span
            aria-hidden="true"
            className="col-start-1 row-start-1 size-full rounded-full bg-[#B7220D] drop-shadow-[0_2px_2px_#B7220D]"
          />
          <span className="col-start-1 row-start-1 size-[calc(100%-4px)] place-self-center overflow-hidden rounded-full">
            {currentImage ? (
              <img src={currentImage} alt="" className="size-full object-cover" />
            ) : (
              <span className="grid size-full place-items-center bg-linear-to-b from-[#FBF5DA] to-[#FDEFB0]">
                <svg viewBox="0 0 24 24" className="size-[72%] fill-[#B7220D]">
                  <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5Z" />
                </svg>
              </span>
            )}
          </span>
        </span>
        {/* Lớp dưới (stroke + shadow) và lớp trên (gradient fill) xếp chồng bằng
            grid để glyph khớp tuyệt đối — shadow không lọt qua chữ gradient. */}
        <p className="inline-grid whitespace-nowrap text-[clamp(14px,2.6cqw,20px)] font-extrabold">
          <span
            aria-hidden="true"
            style={{ WebkitTextStroke: "2px #B7220D" }}
            className="col-start-1 row-start-1 text-[#B7220D] [text-shadow:0_2px_2px_#B7220D]"
          >
            {message}
          </span>
          <span className="col-start-1 row-start-1 text-transparent bg-linear-to-b from-[#FBF5DA] to-[#FDEFB0] bg-clip-text">
            {message}
          </span>
        </p>
      </div>
    </section>
  );
};

export default GuessTicker;
